/**
 * Inc2 · Instrumentos de recolección.
 *
 * Máquina de estados edicion → aprobado → asignado → completado. `asignado` es el
 * paso propio de opinión. Al completarse, el instrumento pasa al repositorio
 * reutilizable y ya no vuelve a edición: para modificarlo hay que reutilizarlo,
 * que crea una copia.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type Instrumento from '@/interfaces/Instrumetos'
import {crearInstrumentoService, getInstrumentoByIdService, getInstrumentosService, actualizarInstrumentoService, eliminarInstrumentoService, eliminarInstrumentoForceService, cambiarEstadoInstrumentoService, aplicarInstrumentoService, getRepositorioInstrumentosService, reutilizarInstrumentoService, getDependenciasInstrumentoService, getPreguntasPorGrupoService
} from '@/services/InstrumentosService'
// C-08: única definición de los servicios de grupos (antes duplicados en InstrumentosService)
import { asignarGruposAInstrumentoService, desvincularGrupoDeInstrumentoService } from '@/services/GrupoInteresService'

/** Store `GestionInstrumento`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionInstrumento = defineStore('GestionInstrumento', {
  state: () => ({
    instrumentos: [] as Instrumento[],
    instrumentoActual: null as Instrumento | null,
    urlInstrumentos: `${process.env.VUE_APP_API_BASE_URL}/api/instrumentos`
  }),

  actions: {
    async cargarTodos() {
      const data = await getInstrumentosService(this.urlInstrumentos)
      this.instrumentos = data
      return data
    },

    async getInstrumentoById(id: number) {
      const data = await getInstrumentoByIdService(this.urlInstrumentos, id)
      if (data) this.instrumentoActual = data
      return data
    },

    async crearInstrumento(payload: Omit<Instrumento, 'id_instrumento'> & { fuente?: string; id_grupo_interes?: number; id_version?: number }) {
      const nuevo = await crearInstrumentoService(this.urlInstrumentos, payload)
      if (nuevo) {
        this.instrumentos.push(nuevo)
        this.instrumentoActual = nuevo
      }
      return nuevo
    },

    async actualizarInstrumento(id: number, payload: Partial<Instrumento>) {
      const actualizado = await actualizarInstrumentoService(this.urlInstrumentos, id, payload)
      if (!actualizado) return null

      const idx = this.instrumentos.findIndex(i => i.id_instrumento === id)
      if (idx !== -1) this.instrumentos[idx] = actualizado
      if (this.instrumentoActual?.id_instrumento === id) {
        this.instrumentoActual = actualizado
      }
      return actualizado
    },

    async eliminarInstrumento(id: number): Promise<boolean> {
      const ok = await eliminarInstrumentoService(this.urlInstrumentos, id)
      if (ok) {
        this.instrumentos = this.instrumentos.filter(i => i.id_instrumento !== id)
        if (this.instrumentoActual?.id_instrumento === id) {
          this.instrumentoActual = null
        }
      }
      return ok
    },

    async asignarGrupos(idInstrumento: number, grupos: number[]): Promise<boolean> {
      return await asignarGruposAInstrumentoService(idInstrumento, grupos)
    },

    async desvincularGrupo(idInstrumento: number, idGrupo: number): Promise<boolean> {
      return await desvincularGrupoDeInstrumentoService(idInstrumento, idGrupo)
    },

    async consultarDependencias(id: number) {
      return await getDependenciasInstrumentoService(this.urlInstrumentos, id)
    },

    async getPreguntasPorGrupo(id: number) {
      return await getPreguntasPorGrupoService(this.urlInstrumentos, id)
    },

    async eliminarInstrumentoForce(id: number): Promise<boolean> {
      const ok = await eliminarInstrumentoForceService(this.urlInstrumentos, id)
      if (ok) {
        this.instrumentos = this.instrumentos.filter(i => i.id_instrumento !== id)
        if (this.instrumentoActual?.id_instrumento === id) this.instrumentoActual = null
      }
      return ok
    },

    async cambiarEstado(id: number, estado: 'edicion' | 'aprobado' | 'asignado' | 'completado'): Promise<Instrumento | null> {
      const actualizado = await cambiarEstadoInstrumentoService(this.urlInstrumentos, id, estado)
      if (actualizado) {
        const idx = this.instrumentos.findIndex(i => i.id_instrumento === id)
        if (idx !== -1) this.instrumentos[idx] = actualizado
        if (this.instrumentoActual?.id_instrumento === id) this.instrumentoActual = actualizado
      }
      return actualizado
    },

    async aplicar(id: number, payload: { id_ejecucion: number; ambito: string; id_programa?: number | null; periodo: string }) {
      return await aplicarInstrumentoService(this.urlInstrumentos, id, payload)
    },

    async getRepositorio(): Promise<Instrumento[]> {
      return await getRepositorioInstrumentosService(this.urlInstrumentos)
    },

    async reutilizar(id: number, id_indicador: number): Promise<Instrumento | null> {
      return await reutilizarInstrumentoService(this.urlInstrumentos, id, id_indicador)
    }
  }
})
